import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  CalendarClock,
  ChevronRight,
  Globe2,
  Layers,
  Sparkles,
  Target,
} from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { BiFilterBar, useBiFilters } from "@/components/bi/BiFilters";
import { MetricCard, formatMoneyMap, formatNumber } from "@/components/bi/BiPrimitives";
import { biAnalyticsService } from "@/services/biAnalytics.service";

export const Route = createFileRoute("/bi/")({
  head: () => ({ meta: [{ title: "Business Intelligence — LicitaPro" }] }),
  component: Page,
});

const REPORTS = [
  {
    to: "/bi/mercado",
    icon: Globe2,
    title: "Mercado Público",
    description: "Volumen publicado, montos, canales, estados y estacionalidad de la demanda.",
  },
  {
    to: "/bi/pipeline",
    icon: Target,
    title: "Pipeline comercial",
    description: "Embudo por etapa, montos en juego, antigüedad y tasa de conversión.",
  },
  {
    to: "/bi/organismos",
    icon: Building2,
    title: "Organismos y regiones",
    description: "Ranking de compradores y distribución territorial por volumen y monto.",
  },
  {
    to: "/bi/categorias",
    icon: Layers,
    title: "Categorías y canales",
    description: "Participación por rubro UNSPSC, tipo de licitación y moneda.",
  },
  {
    to: "/bi/ia",
    icon: Sparkles,
    title: "Inteligencia y scoring",
    description: "Distribución de compatibilidad IA, mejores oportunidades y cobertura del motor.",
  },
  {
    to: "/bi/plazos",
    icon: CalendarClock,
    title: "Plazos y vencimientos",
    description: "Cierres críticos, ventanas de postulación y licitaciones vencidas.",
  },
];

function Page() {
  const { filters } = useBiFilters();
  const kpis = useQuery({
    queryKey: ["bi", "kpis", filters],
    queryFn: () => biAnalyticsService.kpis(filters),
  });
  const k = kpis.data;

  return (
    <AppShell
      title="Business Intelligence"
      subtitle="Analítica del mercado público y de tu operación con datos reales"
    >
      <BiFilterBar />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <MetricCard
          label="Licitaciones publicadas"
          value={formatNumber(k?.published ?? 0)}
          hint={`${formatNumber(k?.active ?? 0)} vigentes`}
          loading={kpis.isLoading}
        />
        <MetricCard
          label="Monto publicado"
          value={formatMoneyMap(k?.amounts ?? {})}
          hint={`${formatNumber(k?.withoutAmount ?? 0)} sin monto informado`}
          loading={kpis.isLoading}
        />
        <MetricCard
          label="En seguimiento"
          value={formatNumber(k?.tracked ?? 0)}
          hint={`${formatNumber(k?.submitted ?? 0)} postuladas`}
          loading={kpis.isLoading}
        />
        <MetricCard
          label="Tasa de adjudicación"
          value={k?.awardRate != null ? `${k.awardRate}%` : "—"}
          hint={`${formatNumber(k?.awarded ?? 0)} adjudicadas`}
          loading={kpis.isLoading}
        />
      </div>

      <h2 className="font-display font-semibold text-foreground mb-3">Reportes disponibles</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {REPORTS.map((report) => (
          <Link
            key={report.to}
            to={report.to}
            className="group rounded-xl border border-border bg-card p-5 shadow-elegant hover:border-primary/50 transition"
          >
            <div className="flex items-start gap-3">
              <div className="size-9 rounded-lg bg-primary/10 grid place-items-center shrink-0">
                <report.icon className="size-4 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-foreground flex items-center gap-1">
                  {report.title}
                  <ChevronRight className="size-3.5 text-muted-foreground group-hover:translate-x-0.5 transition" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{report.description}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </AppShell>
  );
}
